import { DEFAULT_LOCALE, type Locale } from "./constants.js";
import enCA from "./locales/en-CA/thirdPartyApi.json" with { type: "json" };
import enUS from "./locales/en-US/thirdPartyApi.json" with { type: "json" };
import esMX from "./locales/es-MX/thirdPartyApi.json" with { type: "json" };
import frCA from "./locales/fr-CA/thirdPartyApi.json" with { type: "json" };
import jaJP from "./locales/ja-JP/thirdPartyApi.json" with { type: "json" };
import paIN from "./locales/pa-IN/thirdPartyApi.json" with { type: "json" };
import ptPT from "./locales/pt-PT/thirdPartyApi.json" with { type: "json" };
import { getThirdPartyApiCredentialFieldNameKey, getThirdPartyApiServiceNameKey } from "./thirdPartyApiKeys.js";

// An object rather than a bare string so per-name extras (e.g. a future `shortName`) can be added
// without a catalog migration.
export type ThirdPartyApiNameEntry = {
    name: string;
};

export type ThirdPartyApiCatalog = {
    /** Keyed by `ThirdPartyApiServiceType`. Must be complete. */
    services: Record<string, ThirdPartyApiNameEntry>;
    /** Form labels keyed by `ThirdPartyApiCredentialField`. Must be complete. */
    credentialFields: Record<string, ThirdPartyApiNameEntry>;
};

export const thirdPartyApiCatalogs: Record<Locale, ThirdPartyApiCatalog> = {
    "en-CA": enCA as ThirdPartyApiCatalog,
    "en-US": enUS as ThirdPartyApiCatalog,
    "fr-CA": frCA as ThirdPartyApiCatalog,
    "es-MX": esMX as ThirdPartyApiCatalog,
    "ja-JP": jaJP as ThirdPartyApiCatalog,
    "pt-PT": ptPT as ThirdPartyApiCatalog,
    "pa-IN": paIN as ThirdPartyApiCatalog,
};

/** Resolves a dot-separated key (e.g. `services.DAVIS_WEATHER_LINK.name`) to its leaf string. */
export function lookupThirdPartyApiCatalogString(catalog: ThirdPartyApiCatalog, key: string): string | undefined {
    const leaf = key.split(".").reduce<unknown>((cursor, segment) => {
        if (cursor === null || typeof cursor !== "object") return undefined;
        return (cursor as Record<string, unknown>)[segment];
    }, catalog);
    return typeof leaf === "string" ? leaf : undefined;
}

// Falls back to DEFAULT_LOCALE when the locale hasn't translated the key.
function resolveName(locale: Locale, key: string): string | undefined {
    return (
        lookupThirdPartyApiCatalogString(thirdPartyApiCatalogs[locale], key) ??
        lookupThirdPartyApiCatalogString(thirdPartyApiCatalogs[DEFAULT_LOCALE], key)
    );
}

/** Falls back to the raw key if the catalog has no entry, so it never throws. */
export function getThirdPartyApiServiceName(serviceType: string, locale: Locale = DEFAULT_LOCALE): string {
    return resolveName(locale, getThirdPartyApiServiceNameKey(serviceType)) ?? serviceType;
}

/** Falls back to the raw key if the catalog has no entry, so it never throws. */
export function getThirdPartyApiCredentialFieldName(credentialField: string, locale: Locale = DEFAULT_LOCALE): string {
    return resolveName(locale, getThirdPartyApiCredentialFieldNameKey(credentialField)) ?? credentialField;
}
